/**
 * Site istatistikleri (v0.1.166, specs/site-istatistikleri.md): GET /site/stats. The statistics of cadgaragepro.com
 * from four sources over the last 30 days: the site's access log (people and their referrer groups, crawlers by their
 * self-reported User-Agent, pages), Google Analytics 4, Google Search Console and Bing Webmaster, with the state of
 * each source. The same words serve the worker's weekly Telegram summary and the panel's Site screen.
 */
import { z } from 'zod';
import { countSchema, isoDateSchema, isoDateTimeSchema } from '../ids.js';
/** The four sources, in the order the page and the summary show them. */
export const SITE_SOURCES = ['server_log', 'ga4', 'search_console', 'bing'];
/** waiting: never read yet (fresh deploy). The others are shop.site_source_state.status. */
export const SITE_SOURCE_STATUSES = ['ok', 'not_connected', 'error', 'no_data', 'waiting'];
export const SITE_SOURCE_LABELS = Object.freeze({
    server_log: 'Sunucu kaydı',
    ga4: 'Google Analytics',
    search_console: 'Google Search Console',
    bing: 'Bing Webmaster',
});
/**
 * Crawlers by their User-Agent, in matching order (the first match wins). The User-Agent is what the client says
 * about itself; nothing verifies it.
 */
export const SITE_CRAWLERS = [
    'google_inspectiontool',
    'googlebot',
    'bingbot',
    'oai_searchbot',
    'chatgpt_user',
    'gptbot',
    'claude_searchbot',
    'claude_user',
    'claudebot',
    'perplexity_user',
    'perplexitybot',
    'applebot',
    'duckduckbot',
    'yandexbot',
    'other_bot',
];
export const SITE_CRAWLER_GROUPS = [
    'google',
    'bing',
    'openai',
    'anthropic',
    'perplexity',
    'apple',
    'duckduckgo',
    'yandex',
    'other',
];
export const SITE_CRAWLER_GROUP = Object.freeze({
    google_inspectiontool: 'google',
    googlebot: 'google',
    bingbot: 'bing',
    oai_searchbot: 'openai',
    chatgpt_user: 'openai',
    gptbot: 'openai',
    claude_searchbot: 'anthropic',
    claude_user: 'anthropic',
    claudebot: 'anthropic',
    perplexity_user: 'perplexity',
    perplexitybot: 'perplexity',
    applebot: 'apple',
    duckduckbot: 'duckduckgo',
    yandexbot: 'yandex',
    other_bot: 'other',
});
export const SITE_CRAWLER_GROUP_LABELS = Object.freeze({
    google: 'Google',
    bing: 'Bing',
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    perplexity: 'Perplexity',
    apple: 'Apple',
    duckduckgo: 'DuckDuckGo',
    yandex: 'Yandex',
    other: 'Diğer botlar',
});
/** The crawler as its User-Agent names it, for the detail lists. */
export const SITE_CRAWLER_LABELS = Object.freeze({
    google_inspectiontool: 'Google-InspectionTool',
    googlebot: 'Googlebot',
    bingbot: 'Bingbot',
    oai_searchbot: 'OAI-SearchBot',
    chatgpt_user: 'ChatGPT-User',
    gptbot: 'GPTBot',
    claude_searchbot: 'Claude-SearchBot',
    claude_user: 'Claude-User',
    claudebot: 'ClaudeBot',
    perplexity_user: 'Perplexity-User',
    perplexitybot: 'PerplexityBot',
    applebot: 'Applebot',
    duckduckbot: 'DuckDuckBot',
    yandexbot: 'YandexBot',
    other_bot: 'Diğer botlar',
});
/**
 * Crawler address verification (26 Oct 2026). The access log keeps a client address shortened to its /24 (IPv4) or
 * /48 (IPv6); a crawler hit counts as verified when that block overlaps a range its operator publishes for the crawler
 * its User-Agent names. The published range files (read daily by the worker's site-crawler-ranges job):
 */
export const SITE_CRAWLER_RANGE_LISTS = [
    'google_common',
    'google_user_triggered',
    'google_user_triggered_google',
    'bing',
    'openai_gptbot',
    'openai_searchbot',
    'openai_chatgpt_user',
    'anthropic',
    'perplexity_bot',
    'perplexity_user',
    'apple',
    'duckduckgo',
];
/** Where each list is published (checked 26 Oct 2026; perplexity.com answers with a redirect to perplexity.ai). */
export const SITE_CRAWLER_RANGE_URLS = Object.freeze({
    google_common: 'https://developers.google.com/static/search/apis/ipranges/googlebot.json',
    google_user_triggered: 'https://developers.google.com/static/search/apis/ipranges/user-triggered-fetchers.json',
    google_user_triggered_google: 'https://developers.google.com/static/search/apis/ipranges/user-triggered-fetchers-google.json',
    bing: 'https://www.bing.com/toolbox/bingbot.json',
    openai_gptbot: 'https://openai.com/gptbot.json',
    openai_searchbot: 'https://openai.com/searchbot.json',
    openai_chatgpt_user: 'https://openai.com/chatgpt-user.json',
    anthropic: 'https://claude.com/crawling/bots.json',
    perplexity_bot: 'https://www.perplexity.com/perplexitybot.json',
    perplexity_user: 'https://www.perplexity.com/perplexity-user.json',
    apple: 'https://search.developer.apple.com/applebot.json',
    duckduckgo: 'https://duckduckgo.com/duckduckbot.json',
});
/**
 * The lists that verify each crawler. Google's crawlers take Google's three lists (the common crawlers, which include
 * Google-InspectionTool, and both user-triggered fetcher lists); OpenAI and Perplexity publish one list per crawler;
 * Anthropic one list for all three of its crawlers. Yandex publishes no range file (it asks for a reverse DNS check),
 * so YandexBot stays unverified with "no list"; other_bot names no crawler and is not checked.
 */
export const SITE_CRAWLER_RANGES = Object.freeze({
    google_inspectiontool: ['google_common', 'google_user_triggered', 'google_user_triggered_google'],
    googlebot: ['google_common', 'google_user_triggered', 'google_user_triggered_google'],
    bingbot: ['bing'],
    oai_searchbot: ['openai_searchbot'],
    chatgpt_user: ['openai_chatgpt_user'],
    gptbot: ['openai_gptbot'],
    claude_searchbot: ['anthropic'],
    claude_user: ['anthropic'],
    claudebot: ['anthropic'],
    perplexity_user: ['perplexity_user'],
    perplexitybot: ['perplexity_bot'],
    applebot: ['apple'],
    duckduckbot: ['duckduckgo'],
    yandexbot: [],
    other_bot: [],
});
/**
 * The verdict of a crawler hit as shop.site_log_daily.ip_check stores it: verified, unverified (its operator's list was
 * read and the address is not in it, or the line had no address), no_list (the operator publishes no list), or ''
 * (not checked: a person, other_bot, a hit counted before 26 Oct 2026, or no copy of the list read yet).
 */
export const SITE_IP_CHECKS = ['', 'verified', 'unverified', 'no_list'];
/** A crawler's hits in a range by verdict (every request, assets and files included). */
export const siteCrawlerCheckSchema = z.strictObject({
    /** A SITE_CRAWLERS key (other_bot left out). */
    key: z.string().min(1).max(40),
    claimed: countSchema,
    verified: countSchema,
    unverified: countSchema,
    noList: countSchema,
    unchecked: countSchema,
});
/** The last read of a published range list. */
export const siteCrawlerRangeStateSchema = z.strictObject({
    id: z.enum(SITE_CRAWLER_RANGE_LISTS),
    /** Prefixes in the last good copy (0 before the first good read). */
    prefixes: countSchema,
    /** When the last good copy was read; null before the first. */
    fetchedAt: isoDateTimeSchema.nullable(),
    /** The last try, good or not. */
    checkedAt: isoDateTimeSchema.nullable(),
    /** A one-word reason when the last try failed (the good copy stays). */
    error: z.string().max(40).nullable(),
});
/** Where a person came from, by the Referer host (and utm_source for the AI assistants). */
export const SITE_REFERRERS = [
    'internal',
    'chatgpt',
    'perplexity',
    'claude',
    'gemini',
    'copilot',
    'bing',
    'google',
    'etsy',
    'other',
    'none',
];
/** The AI assistants among the referrer groups. */
export const SITE_AI_REFERRERS = Object.freeze([
    'chatgpt',
    'perplexity',
    'claude',
    'gemini',
    'copilot',
]);
export const SITE_REFERRER_LABELS = Object.freeze({
    internal: 'Site içi',
    chatgpt: 'ChatGPT',
    perplexity: 'Perplexity',
    claude: 'Claude',
    gemini: 'Gemini',
    copilot: 'Copilot',
    bing: 'Bing (Copilot dahil olabilir)',
    google: 'Google',
    etsy: 'Etsy',
    other: 'Diğer siteler',
    none: 'Doğrudan',
});
/** utm_source reduced to a bounded set; anything else is "other". */
export const SITE_UTM_SOURCES = [
    'chatgpt.com',
    'perplexity',
    'claude.ai',
    'gemini',
    'copilot',
    'google',
    'bing',
    'etsy',
    'outreach',
    'other',
];
/** The page path row that takes the paths over a day's cap. */
export const SITE_OTHER_PATH = '(other)';
export const siteSourceStateSchema = z.strictObject({
    id: z.enum(SITE_SOURCES),
    status: z.enum(SITE_SOURCE_STATUSES),
    /** Turkish: what was read, or why not (never a key). */
    detailTr: z.string().max(300).nullable(),
    checkedAt: isoDateTimeSchema.nullable(),
    lastOkAt: isoDateTimeSchema.nullable(),
    /** The last day the source has data for. */
    dataThrough: isoDateSchema.nullable(),
});
export const siteCountSchema = z.strictObject({ key: z.string().max(300), count: countSchema });
/**
 * v0.1.235: the site's "Buy on Etsy" links go through /go/<product slug>/<placement>/ (the site's catalog.ts goPath),
 * so the server log counts the clicks of people (browser signs, coming from our own page) per product and placement.
 */
export const siteBuyClickSchema = z.strictObject({
    product: z.string().min(1).max(120),
    placement: z.string().min(1).max(40),
    count: countSchema,
});
/**
 * Where the visitor who clicked an Etsy link came from (plan item 2, 25 Oct 2026): the referrer group of the same
 * client's last arrival from outside in the same log file of the same hourly read, "outreach" for a wave mail's link,
 * "unknown" when the arrival was not there (an earlier read, or a file rolled over in between). The client is known
 * only in memory while the read runs; nothing about it is stored.
 */
export const SITE_CLICK_SOURCES = [
    'chatgpt',
    'perplexity',
    'claude',
    'gemini',
    'copilot',
    'bing',
    'google',
    'etsy',
    'outreach',
    'other',
    'none',
    'unknown',
];
export const SITE_CLICK_SOURCE_LABELS = Object.freeze({
    chatgpt: 'ChatGPT',
    perplexity: 'Perplexity',
    claude: 'Claude',
    gemini: 'Gemini',
    copilot: 'Copilot',
    bing: 'Bing (Copilot dahil olabilir)',
    google: 'Google',
    etsy: 'Etsy',
    outreach: 'Tanıtım mailleri',
    other: 'Diğer siteler',
    none: 'Doğrudan',
    unknown: 'Bilinmiyor',
});
/** The fetches of the six AI user and search agents (the agent counts of siteAiPageSchema and siteAiTotalsSchema). */
export const siteAiAgentCountsSchema = z.strictObject({
    chatgptUser: countSchema,
    oaiSearchbot: countSchema,
    perplexityUser: countSchema,
    perplexitybot: countSchema,
    claudeUser: countSchema,
    claudeSearchbot: countSchema,
});
/**
 * One page of the site with what the AI services did with it (plan item 2, 25 Oct 2026). Fetches are successful page
 * (or well-known file) requests of the crawler as its User-Agent names it (self-reported; since 26 Oct 2026 `verified`
 * holds the ones whose address is in the crawler's published list): ChatGPT-User, Perplexity-User and Claude-User
 * open a page live for a person's question, OAI-SearchBot, PerplexityBot and Claude-SearchBot read it for their
 * search index.
 */
export const siteAiPageSchema = z.strictObject({
    path: z.string().min(1).max(200),
    chatgptUser: countSchema,
    oaiSearchbot: countSchema,
    perplexityUser: countSchema,
    perplexitybot: countSchema,
    claudeUser: countSchema,
    claudeSearchbot: countSchema,
    /** People who arrived on this page from ChatGPT (Referer chatgpt.com, or utm_source=chatgpt.com). */
    chatgptEntries: countSchema,
    /** People who arrived on this page from any AI assistant (SITE_AI_REFERRERS), ChatGPT included. */
    aiEntries: countSchema,
    /** Clicks on the site's Etsy links made on this page (/go/ pages whose Referer is this page). */
    etsyClicks: countSchema,
    /** Those clicks by where the visitor came from (SITE_CLICK_SOURCES keys), most first. */
    etsyBySource: z.array(siteCountSchema),
    /**
     * A well-known file (robots.txt, sitemap*.xml, llms.txt), not a page: the crawlers read it before the pages, which is
     * neither a live open nor an index read. Listed after the pages and left out of siteAiTotals.
     */
    file: z.boolean().optional(),
    /** 26 Oct 2026: the same six fetch counts, only the hits whose address is in the crawler's published list. */
    verified: siteAiAgentCountsSchema.optional(),
    /**
     * The same six counts, only the hits checked against the crawler's list and not in it. The rest of the claimed
     * fetches (claimed - verified - unverified) were not checked: counted before 0117 or before the list's first read.
     */
    unverified: siteAiAgentCountsSchema.optional(),
});
/** The same counts over every page of the range (pages only, the well-known files left out). */
export const siteAiTotalsSchema = z.strictObject({
    chatgptUser: countSchema,
    oaiSearchbot: countSchema,
    perplexityUser: countSchema,
    perplexitybot: countSchema,
    claudeUser: countSchema,
    claudeSearchbot: countSchema,
    chatgptEntries: countSchema,
    aiEntries: countSchema,
});
export const siteLogSummarySchema = z.strictObject({
    /** Page views of people whose referrer is not the site itself: arrivals from outside. */
    entries: countSchema,
    pageViews: countSchema,
    /** Entries from the AI assistants (SITE_AI_REFERRERS). */
    aiEntries: countSchema,
    /** Every request of a crawler, assets included. */
    crawlerHits: countSchema,
    /** Entries by referrer group, most first (internal left out). */
    referrers: z.array(siteCountSchema),
    /** AI entries by assistant, most first. */
    assistants: z.array(siteCountSchema),
    /** Crawler requests by group (SITE_CRAWLER_GROUPS), most first. */
    crawlers: z.array(siteCountSchema),
    /** Crawler requests by crawler (SITE_CRAWLERS), most first. */
    crawlerAgents: z.array(siteCountSchema),
    /** Page views of people by path, most first (at most 10). */
    topPages: z.array(siteCountSchema),
    /** v0.1.235: clicks on the site's Etsy links (/go/ pages) by product and placement, most first (at most 20). */
    buyClicks: z.array(siteBuyClickSchema).optional(),
    /** v0.1.235: every such click in the range. */
    buyClickTotal: countSchema.optional(),
    /**
     * Plan item 2 (25 Oct 2026): the clicks on the site's Etsy links by where the visitor came from (SITE_CLICK_SOURCES
     * keys), most first. Counted from this release on, so the sum can be below buyClickTotal.
     */
    buyClickSources: z.array(siteCountSchema).optional(),
    /** Plan item 2: the pages the AI services fetched or sent people to, busiest first (at most 30). */
    aiPages: z.array(siteAiPageSchema).optional(),
    /** Plan item 2: the same counts over all pages. */
    aiTotals: siteAiTotalsSchema.optional(),
    /** 26 Oct 2026: aiTotals' six fetch counts, only the hits whose address is in the crawler's published list. */
    aiVerified: siteAiAgentCountsSchema.optional(),
    /** aiTotals' six fetch counts checked against the crawler's list and not in it (the rest of the gap: not checked). */
    aiUnverified: siteAiAgentCountsSchema.optional(),
    /** 26 Oct 2026: every named crawler's hits by verdict (address verification), most claimed first. */
    crawlerChecks: z.array(siteCrawlerCheckSchema).optional(),
});
/**
 * "Wanted scans" (plan item 13, 25 Oct 2026): the vehicles people asked us for, with the part of the vehicle, counted
 * from what Twin already keeps: the site assistant's questions it handed to the team, the site's forms (questions,
 * free CAD jobs, "notify me"), the questions the agents could not answer, custom job requests and the demand pool.
 * One request is one chat, conversation or signal, counted once per vehicle. Only aggregates leave the database: no
 * question text, name or address.
 */
export const SITE_WANTED_AREAS = [
    'full_vehicle',
    'front',
    'rear',
    'chassis',
    'tray_bed',
    'roof',
    'underbody',
    'sliders',
    'interior',
    'fenders',
    'unspecified',
];
export const SITE_WANTED_AREA_LABELS = Object.freeze({
    full_vehicle: 'Komple araç',
    front: 'Ön (tampon, ön uç)',
    rear: 'Arka (tampon, çeki)',
    chassis: 'Şasi',
    tray_bed: 'Kasa, tepsi, kanopi',
    roof: 'Tavan, port bagaj',
    underbody: 'Alt gövde',
    sliders: 'Marşpiye, yan koruma',
    interior: 'İç mekân',
    fenders: 'Çamurluk',
    unspecified: 'Bölge belirtilmemiş',
});
export const SITE_WANTED_SOURCES = [
    'site_assistant',
    'site_form',
    'notify',
    'question',
    'custom_request',
    'demand_signal',
];
export const SITE_WANTED_SOURCE_LABELS = Object.freeze({
    site_assistant: 'Site asistanı',
    site_form: 'Site formu',
    notify: 'Satışa çıkınca haber ver',
    question: 'Cevaplanamayan soru',
    custom_request: 'Özel iş talebi',
    demand_signal: 'Talep havuzu',
});
/** on_sale: a product of this vehicle has an active Etsy listing; in_catalog: only unlisted products; none: no product. */
export const SITE_WANTED_CATALOG = ['on_sale', 'in_catalog', 'none'];
export const SITE_WANTED_CATALOG_LABELS = Object.freeze({
    on_sale: 'Satışta',
    in_catalog: 'Katalogda, satışta değil',
    none: 'Elimizde yok',
});
export const siteWantedScanSchema = z.strictObject({
    /**
     * "Toyota Hilux N80", "Toyota Tacoma (2024)", "Toyota Hilux N50" (a generation word none of ours carries; catalog
     * none) or "Toyota Tacoma (nesil belirtilmemiş)".
     */
    vehicle: z.string().min(1).max(160),
    make: z.string().min(1).max(60),
    model: z.string().min(1).max(80),
    /** The generation of our vehicle list the requests named (by code, alias or year); null when none. */
    generation: z.string().max(80).nullable(),
    /** Years the requests named that no generation of ours covers (a newer model, for example). */
    years: z.array(z.number().int().min(1950).max(2100)).max(10),
    requests: countSchema,
    /** Requests of the last seven days of the window. */
    recent: countSchema,
    areas: z.array(siteCountSchema),
    sources: z.array(siteCountSchema),
    catalog: z.enum(SITE_WANTED_CATALOG),
    /** Kontrol listesi B3: the SKUs on sale that the row matched (generation or model), at most five. */
    listings: z.array(z.string().min(1).max(60)).max(5).optional(),
    lastAt: isoDateSchema,
});
export const siteWantedSchema = z.strictObject({
    from: isoDateSchema,
    to: isoDateSchema,
    /** Every request read in the window, with or without a vehicle we could tell. */
    requests: countSchema,
    /** Requests that named no vehicle we could tell. */
    unmatched: countSchema,
    /** Most requested first (at most 30). */
    rows: z.array(siteWantedScanSchema),
});
/**
 * v0.1.216 (Sercan, 20 Oct 2026: "gün gün ziyaretleri görelim, grafiklerde tüm takip ettiğimiz kaynaklar"): GA4's
 * session sources in the groups the Site screen draws, in the order the chart stacks them.
 */
export const SITE_SOURCE_GROUPS = [
    'google',
    'chatgpt',
    'other_ai',
    'bing',
    'outreach',
    'etsy',
    'direct',
    'other',
];
export const SITE_SOURCE_GROUP_LABELS = Object.freeze({
    google: 'Google',
    chatgpt: 'ChatGPT',
    other_ai: 'Diğer yapay zekâ',
    bing: 'Bing ve DuckDuckGo',
    outreach: 'Tanıtım mailleri',
    etsy: 'Etsy',
    direct: 'Doğrudan',
    other: 'Diğer siteler',
});
/** The group of a GA4 sessionSource ("google", "chatgpt.com", "outreach", "(direct)", ...). */
export function siteSourceGroup(source) {
    const value = source.trim().toLowerCase();
    if (value === '(direct)' || value === '(not set)' || value === '')
        return 'direct';
    if (value === 'outreach')
        return 'outreach';
    if (value === 'chatgpt.com' || value === 'chat.openai.com' || value === 'openai')
        return 'chatgpt';
    if (/(^|\.)(gemini\.google\.com|perplexity\.ai|claude\.ai|copilot\.microsoft\.com)$|^(perplexity|gemini|claude|copilot)$/.test(value)) {
        return 'other_ai';
    }
    if (value === 'google' || /(^|\.)google\.[a-z.]+$/.test(value))
        return 'google';
    if (value === 'bing' || value === 'duckduckgo' || /(^|\.)(bing\.com|duckduckgo\.com)$/.test(value))
        return 'bing';
    if (value === 'etsy' || /(^|\.)etsy\.com$/.test(value))
        return 'etsy';
    return 'other';
}
export const siteGa4RowSchema = z.strictObject({ key: z.string().max(200), sessions: countSchema, users: countSchema });
export const siteGa4SummarySchema = z.strictObject({
    sessions: countSchema,
    /** The daily user totals added up: a person who came on two days counts twice. */
    users: countSchema,
    views: countSchema,
    etsyClicks: countSchema,
    channels: z.array(siteGa4RowSchema),
    sources: z.array(siteGa4RowSchema),
    topPages: z.array(siteCountSchema),
    etsyClickPages: z.array(siteCountSchema),
    /** v0.1.216: Etsy clicks by the source group of the visit (SITE_SOURCE_GROUPS keys), most first. */
    etsyClickSources: z.array(siteCountSchema),
});
export const siteSearchRowSchema = z.strictObject({
    key: z.string().max(300),
    clicks: countSchema,
    impressions: countSchema,
    ctr: z.number().min(0).max(1).nullable(),
    position: z.number().nonnegative().nullable(),
});
export const siteSearchSummarySchema = z.strictObject({
    clicks: countSchema,
    impressions: countSchema,
    ctr: z.number().min(0).max(1).nullable(),
    /** Impression weighted average position; null when the engine does not give one for the totals. */
    position: z.number().nonnegative().nullable(),
    /** The last day with data in the range. */
    dataThrough: isoDateSchema.nullable(),
    /** At least one day of the range is not final yet (Search Console). */
    partial: z.boolean(),
    topQueries: z.array(siteSearchRowSchema),
    topPages: z.array(siteSearchRowSchema),
});
export const siteDaySchema = z.strictObject({
    day: isoDateSchema,
    entries: countSchema,
    pageViews: countSchema,
    aiEntries: countSchema,
    crawlerHits: countSchema,
    /** GA4 sessions of the day; null when GA4 has no row for it. */
    sessions: countSchema.nullable(),
    /** v0.1.216: GA4 sessions of the day by source group (SITE_SOURCE_GROUPS keys); empty without GA4 rows. */
    sourceSessions: z.partialRecord(z.enum(SITE_SOURCE_GROUPS), countSchema),
    googleClicks: countSchema.nullable(),
    googleImpressions: countSchema.nullable(),
});
/**
 * Bot gözü testi (26 Oct 2026; Sercan approved the "bot gözü testi": Twin opens the important pages the way the search
 * and AI crawlers do, every week, to catch a server problem early). The agents of the test, the browser first: its
 * answer is the one the crawlers' answers are compared with. The crawler keys are SITE_CRAWLERS keys, so their labels
 * are SITE_CRAWLER_LABELS.
 */
export const SITE_BOT_EYE_AGENTS = [
    'browser',
    'googlebot',
    'bingbot',
    'oai_searchbot',
    'chatgpt_user',
    'claudebot',
    'perplexitybot',
];
export const SITE_BOT_EYE_PAGE_KINDS = ['home', 'product', 'vehicle', 'guide', 'robots', 'sitemap', 'llms'];
export const SITE_BOT_EYE_PAGE_KIND_LABELS = Object.freeze({
    home: 'Ana sayfa',
    product: 'Ürün',
    vehicle: 'Araç',
    guide: 'Rehber',
    robots: 'robots.txt',
    sitemap: 'Site haritası',
    llms: 'llms.txt',
});
/**
 * What a check found wrong. status, timeout, network, no_h1 and no_key make the check fail (the Telegram warning;
 * no_key on a product or vehicle page only for a crawler that lacks the price or measured block the browser's page had:
 * the browser's page without one is a finding, hasKey false, as some real pages have none);
 * robots (robots.txt keeps that crawler out of the page), cloaking (the crawler got other content than the browser),
 * markdown (the page links a Markdown copy but an Accept: text/markdown request did not get one) and noindex (the
 * Markdown answer has no X-Robots-Tag: noindex) are findings of the weekly line and the panel.
 */
export const SITE_BOT_EYE_PROBLEMS = [
    'status',
    'timeout',
    'network',
    'no_h1',
    'no_key',
    'robots',
    'cloaking',
    'markdown',
    'noindex',
];
export const SITE_BOT_EYE_PROBLEM_LABELS = Object.freeze({
    status: 'cevap 200 değil',
    timeout: 'zaman aşımı (20 sn, iki deneme)',
    network: 'bağlantı hatası',
    no_h1: 'başlık (h1) yok',
    no_key: 'ana içerik yok (tarayıcıda görünen fiyat ya da ölçü bloğu, ya da dosya biçimi)',
    robots: 'robots.txt bu botu engelliyor',
    cloaking: 'tarayıcıdakinden farklı içerik',
    markdown: 'Markdown kopyası istenince gelmedi',
    noindex: 'Markdown cevabında noindex yok',
});
/** One page as one agent saw it (variant markdown: the browser with Accept: text/markdown). */
export const siteBotEyeCheckSchema = z.strictObject({
    path: z.string().min(1).max(200),
    kind: z.enum(SITE_BOT_EYE_PAGE_KINDS),
    agent: z.enum(SITE_BOT_EYE_AGENTS),
    variant: z.enum(['html', 'markdown']),
    /** The HTTP status of the last attempt; null when no answer came (timeout, network). */
    status: z.number().int().min(100).max(599).nullable(),
    /** Time to the answer's headers and to its last byte, of the last attempt. */
    ttfbMs: countSchema.nullable(),
    totalMs: countSchema.nullable(),
    bytes: countSchema.nullable(),
    /** 1, or 2 when the first attempt timed out, failed or answered 429 or 5xx. */
    attempts: z.number().int().min(1).max(2),
    /** Null where it does not apply (files, the Markdown variant) or nothing came. */
    hasH1: z.boolean().nullable(),
    /** As hasH1; false on the browser's product or vehicle page is a finding of the page, not a failure. */
    hasKey: z.boolean().nullable(),
    /** Null for the browser and when robots.txt could not be read. */
    robotsAllowed: z.boolean().nullable(),
    /** Null for the browser, the Markdown variant and when either answer is missing. */
    sameAsBrowser: z.boolean().nullable(),
    /** The Markdown variant only. */
    noindex: z.boolean().nullable(),
    ok: z.boolean(),
    problems: z.array(z.enum(SITE_BOT_EYE_PROBLEMS)).max(SITE_BOT_EYE_PROBLEMS.length),
});
export const siteBotEyeRunSchema = z.strictObject({
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema,
    pages: countSchema,
    agents: countSchema,
    requests: countSchema,
    /** HTML checks that failed (status, timeout, network, no_h1, no_key). */
    failed: countSchema,
    /** live: at least one page linked a Markdown copy and the copies were checked; not_live: no page linked one. */
    markdown: z.enum(['live', 'not_live']),
    /** The request cap (under 150) ended the run before every check. */
    stopped: z.boolean(),
    checks: z.array(siteBotEyeCheckSchema).max(200),
});
export const siteStatsViewSchema = z.strictObject({
    generatedAt: isoDateTimeSchema,
    /** The days of the server log (Europe/Istanbul); GA4 uses the property's zone, Search Console Pacific Time. */
    timeZone: z.string().min(1).max(64),
    from: isoDateSchema,
    to: isoDateSchema,
    /** The 30 days, oldest first. */
    days: z.array(isoDateSchema),
    sources: z.array(siteSourceStateSchema),
    log: siteLogSummarySchema,
    /** Null when GA4 has no row in the range. */
    ga4: siteGa4SummarySchema.nullable(),
    google: siteSearchSummarySchema.nullable(),
    bing: siteSearchSummarySchema.nullable(),
    daily: z.array(siteDaySchema),
    /** Plan item 13 (25 Oct 2026): the wanted scans of the last 90 days. */
    wanted: siteWantedSchema.optional(),
    /** Bot gözü testi (26 Oct 2026): the last weekly run; null before the first one. */
    botEye: siteBotEyeRunSchema.nullable().optional(),
    /** Crawler address verification (26 Oct 2026): the published range lists and their last reads. */
    crawlerRanges: z.array(siteCrawlerRangeStateSchema).optional(),
});
//# sourceMappingURL=site-stats.js.map